import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './teamBanner.module.scss';
import { division } from '../utilities/utilities';
import { getEnvConfig, countries } from '../../config/config';
import ReactCountryFlag from 'react-country-flag';
import SeasonSelect from '../seasonSelect/seasonSelect';
import { FilterOption } from '../../context/active-filter-context';
import { WTNScaleInterface } from '../../graphql-types/wtn';
import WTNTeamBox from '../wtnTeamBox/wtnTeamBox';
import { WTNScale } from '@clubspark-react/clubspark-itf-shared-components';

interface TeamBannerProps {
  teamData: any;
  thisTeam: any;
  thisPlayer?: any;
  view?: string;
  options: FilterOption[];
  wtnData?: Partial<WTNScaleInterface>;
  wtnTeamAverage?: number;
}

const TeamBanner: React.FC<TeamBannerProps> = ({
  teamData,
  thisTeam,
  view,
  thisPlayer,
  options,
  wtnData,
  wtnTeamAverage
}) => {

  const [imageLoaded, setImageLoaded] = useState(false);
  const { t } = useTranslation();
  const wtnPlayerSearchUrl = getEnvConfig().wtnPlayerSearchUrl;

  let teamGender = useMemo(() => {
    if (
      typeof thisTeam.teamId !== 'undefined' &&
      thisTeam.teamId !== null &&
      typeof teamData.manId !== 'undefined' &&
      teamData.manId !== null
    ) {
      if (thisTeam.teamId.toUpperCase() === teamData.manId.toUpperCase()) {
        return "MEN";
      }
    }

    if (
      typeof thisTeam.teamId !== 'undefined' &&
      thisTeam.teamId !== null &&
      typeof teamData.womanId !== 'undefined' &&
      teamData.womanId !== null
    ) {
      if (thisTeam.teamId.toUpperCase() === teamData.womanId.toUpperCase()) {
        return "WOMEN";
      }
    }

  }, []);

  const teamType = () => {
    if (teamGender === "MEN"){
      return t('mensTeam');
    }    
    if (teamGender === "WOMEN"){
      return t('womensTeam');
    }
    return '';
  };

  const siteUrl = /\/$/.test(getEnvConfig().site) ? getEnvConfig().site : `${getEnvConfig().site}/`;
  const teamUrl = getEnvConfig().teamUrl;

  let image = useMemo(() => new Image(), []);

  const loaded = () => {
    setImageLoaded(true);
  };

  switch (view) {
    case 'team':
      image.src = `${siteUrl}${thisTeam.teamId}/Logo`;
      break;
    case 'player':
      image.src = thisPlayer?.avatarUrl;
      break;
  }

  useEffect(() => {
    image.addEventListener('load', loaded);

    return () => {
      image.removeEventListener('load', loaded);
    };
  }, [image]);

  let divisionVal = division(teamData.division);

  let playerCountryCode: string | any = null;
  let playerCountry: string | any = null;
  if (thisPlayer?.nationalityCode) {
    if (countries[thisPlayer?.nationalityCode]) {
      playerCountryCode = countries[thisPlayer?.nationalityCode]['alpha-2'];
      playerCountry = countries[thisPlayer?.nationalityCode]['name'];
    }
  }

  return (
    <div>
      <div className={`${styles.teamBanner} ${view === 'player' ? styles.playerBanner : null}`}>
        <div className={styles.siteWidth}>
          {imageLoaded && (
            <div className={styles.teamLogo}>
              <div className={`${styles.avatar} ${view === 'team' ? styles.highlight : null}`}>
                <img src={image.src} alt="" />
              </div>
            </div>
          )}

          <div className={styles.teamDetailsContainer}>
            <div className={styles.teamDetails}>
              {view && view === 'team' && (
                <>
                  <h1 className={styles.teamName}>{teamData.name}</h1>
                  <p>{teamType()}</p>
                </>
              )}

              {view && view === 'player' && (
                <>
                  <h1 className={styles.playerName}>
                    <span>{thisPlayer?.standardGivenName}</span>&nbsp;
                    <strong>{thisPlayer?.standardFamilyName}</strong>&nbsp;
                    <small>
                      {playerCountryCode !== null &&
                      playerCountry !== null &&
                      playerCountryCode !== 'RU' &&
                      playerCountryCode !== 'BY' ? (
                        <ReactCountryFlag
                          countryCode={playerCountryCode}
                          svg
                          alt={playerCountry}
                          title={playerCountry}
                        />
                      ) : null}
                    </small>
                  </h1>
                  {thisPlayer?.tennisId && (
                    <p className={styles.tennisId}>
                      {t('wtnTennisId')}:&nbsp;
                      <a href={`${wtnPlayerSearchUrl}${thisPlayer.tennisId}`}>
                        {thisPlayer.tennisId}
                      </a>
                    </p>
                  )}

                  <p className={styles.playerTeam}>
                    <a href={`${teamUrl}${thisTeam.teamId}&a=Index&c=VenueTeam`}>{teamData.name}</a>
                  </p>
                </>
              )}

              <div className={styles.teamInfoItem}>
                <h2>{t('division')}</h2>
                <p className={styles.teamInfoItemText}>{divisionVal}</p>
              </div>

              <div className={styles.teamInfoItem}>
                <h2>{t('conference')}</h2>
                <p className={styles.teamInfoItemText}>
                  {teamGender === "MEN" && teamData.manConference && (t(teamData.manConference))}
                  {teamGender === "WOMEN" && teamData.womanConference && (t(teamData.womanConference))}
                </p>
              </div>

              {view && view === 'player' && (
                <div className={styles.teamInfoItem}>
                  <h2>{t('class')}</h2>
                  <p>{thisPlayer?.class}</p>
                </div>
              )}

              {view && view === 'team' && (
                <>
                  <div className={styles.teamInfoItem}>
                    <h2>{t('playingRegion')}</h2>
                    <p className={styles.teamInfoItemText}>
                      {teamData.itaRegion.replace(/_/g, ' ')}
                    </p>
                  </div>

                  <div className={styles.teamInfoItem}>
                    <h2>{t('rankingAwardRegion')}</h2>
                    <p className={styles.teamInfoItemText}>
                      {teamData.rankingAwardRegion.replace(/_/g, ' ')}
                    </p>
                  </div>
                </>
              )}
            </div>
            <div className={styles.wtnScaleContainer}>
              {view === 'player' && (
                <WTNScale
                  wtnSingles={wtnData?.tennisNumberSingles}
                  confidenceSingles={wtnData?.confidenceSingles}
                  wtnDoubles={wtnData?.tennisNumberDoubles}
                  confidenceDoubles={wtnData?.confidenceDoubles}
                  proPlayerSingles={wtnData?.isRankedSingles}
                  proPlayerDoubles={wtnData?.isRankedDoubles}
                  mode=""
                  showTwoDecimalPoints
                />
              )}
              {view === 'team' && <WTNTeamBox title={t('teamWTN')} tennisNumber={wtnTeamAverage} />}
            </div>
          </div>
        </div>
      </div>
      <SeasonSelect options={options} />
    </div>
  );
};

export default TeamBanner;
